(() => {
  "use strict";

  const SELECTORS = {
    section: ".product-slider-section",
    tab: ".product-slider__tab",
    panel: ".product-slider__panel",
    embla: "[data-product-slider]",
    prevButton: "[data-slider-prev]",
    nextButton: "[data-slider-next]",
  };

  const CLASSES = {
    activeTab: ["text-primary", "underline", "underline-offset-4"],
    inactiveTab: ["text-subdued", "hover:text-primary"],
    hidden: "hidden",
  };

  /**
   * Initialize a single Embla carousel instance
   * @param {HTMLElement} emblaNode - The carousel container element
   * @returns {Object} Embla carousel instance
   */
  const initializeEmbla = (emblaNode) =>
    window.EmblaCarousel(emblaNode, {
      loop: false,
      align: "start",
      skipSnaps: false,
      dragFree: true,
      containScroll: "trimSnaps",
    });

  /**
   * Update navigation button states based on carousel position
   * @param {Object} embla - Embla carousel instance
   * @param {HTMLElement} prevButton - Previous button element
   * @param {HTMLElement} nextButton - Next button element
   */
  const updateButtonStates = (embla, prevButton, nextButton) => {
    if (!embla || !prevButton || !nextButton) return;

    prevButton.disabled = !embla.canScrollPrev();
    nextButton.disabled = !embla.canScrollNext();
  };

  /**
   * Switch to a specific tab and panel
   * @param {HTMLElement} section - The section container
   * @param {number} tabIndex - Index of the tab to activate
   * @param {Map} emblaInstances - Map of Embla instances
   */
  const switchTab = (section, tabIndex, emblaInstances) => {
    const tabs = section.querySelectorAll(SELECTORS.tab);
    const panels = section.querySelectorAll(SELECTORS.panel);
    const prevButton = section.querySelector(SELECTORS.prevButton);
    const nextButton = section.querySelector(SELECTORS.nextButton);

    tabs.forEach((tab) => {
      const isActive = parseInt(tab.dataset.tabIndex, 10) === tabIndex;

      tab.setAttribute("aria-selected", String(isActive));
      tab.classList.remove(...CLASSES.activeTab, ...CLASSES.inactiveTab);
      tab.classList.add(
        ...(isActive ? CLASSES.activeTab : CLASSES.inactiveTab),
      );
    });

    panels.forEach((panel) => {
      const isActive = parseInt(panel.dataset.panelIndex, 10) === tabIndex;
      panel.classList.toggle(CLASSES.hidden, !isActive);

      if (!isActive) return;

      const emblaNode = panel.querySelector(SELECTORS.embla);
      if (!emblaNode) return;

      let embla = emblaInstances.get(emblaNode);

      if (!embla) {
        embla = initializeEmbla(emblaNode);
        emblaInstances.set(emblaNode, embla);

        embla.on("select", () => {
          if (panel.classList.contains(CLASSES.hidden)) return;
          updateButtonStates(embla, prevButton, nextButton);
        });
        embla.on("reInit", () => {
          if (panel.classList.contains(CLASSES.hidden)) return;
          updateButtonStates(embla, prevButton, nextButton);
        });
      } else {
        embla.reInit();
      }

      updateButtonStates(embla, prevButton, nextButton);
    });
  };

  /**
   * Get the active Embla instance for a section
   * @param {HTMLElement} section - The section container
   * @param {Map} emblaInstances - Map of Embla instances
   * @returns {Object|null} Active Embla instance or null
   */
  const getActiveEmbla = (section, emblaInstances) => {
    const activePanel = section.querySelector(
      `${SELECTORS.panel}:not(.${CLASSES.hidden})`,
    );
    const emblaNode = activePanel?.querySelector(SELECTORS.embla);
    return emblaNode ? emblaInstances.get(emblaNode) : null;
  };

  /**
   * Initialize a product slider section
   * @param {HTMLElement} section - The section container
   */
  const initializeSection = (section) => {
    const tabs = section.querySelectorAll(SELECTORS.tab);
    const prevButton = section.querySelector(SELECTORS.prevButton);
    const nextButton = section.querySelector(SELECTORS.nextButton);
    const emblaInstances = new Map();

    if (tabs.length === 0) return;

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const tabIndex = parseInt(tab.dataset.tabIndex, 10);
        switchTab(section, tabIndex, emblaInstances);
      });
    });

    prevButton?.addEventListener("click", () => {
      getActiveEmbla(section, emblaInstances)?.scrollPrev();
    });

    nextButton?.addEventListener("click", () => {
      getActiveEmbla(section, emblaInstances)?.scrollNext();
    });

    switchTab(section, 0, emblaInstances);

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const embla = getActiveEmbla(section, emblaInstances);
        if (embla) {
          embla.reInit();
          updateButtonStates(embla, prevButton, nextButton);
        }
      }, 100);
    });
  };

  const init = () => {
    document.querySelectorAll(SELECTORS.section).forEach(initializeSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
